// ============================================================================
// SetEditor — the add-a-set sheet. Pick an exercise (via ExercisePicker), then
// reps / weight / RPE / RIR / side / notes. Live volume preview (reps × weight)
// echoes the backend formula. Wires to useAddSet. Optimised for fast, repeated
// one-handed entry: large numeric inputs, sticky "Add" bar, and the exercise
// + side persist between adds so logging set-after-set is a couple of taps.
// ============================================================================
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, SelectField, TextField } from "../../components";
import { useAddSet } from "../../hooks/sessions";
import type { Exercise, SetInput } from "../../api";
import { ExercisePicker } from "./ExercisePicker";
import { Sheet } from "./Sheet";
import { exerciseName, fmtNum, parseInt0, parseNum, setVolume } from "./lib";
import { cn } from "../../lib/cn";

interface SetEditorProps {
  open: boolean;
  onClose: () => void;
  sessionId: number;
  /** Next set number (so the field pre-fills sensibly). */
  nextSetNo: number;
}

export function SetEditor({ open, onClose, sessionId, nextSetNo }: SetEditorProps) {
  const { t, i18n } = useTranslation("sessions");
  const addSet = useAddSet();

  const [picker, setPicker] = useState(false);
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [setNo, setSetNo] = useState("");
  const [reps, setReps] = useState("");
  const [weight, setWeight] = useState("");
  const [rpe, setRpe] = useState("");
  const [rir, setRir] = useState("");
  const [side, setSide] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    setSetNo(String(nextSetNo));
    setError(undefined);
  }, [open, nextSetNo]);

  const volume = setVolume({ reps: parseInt0(reps), weight_kg: parseNum(weight) });

  const submit = (keepOpen: boolean) => {
    setError(undefined);
    const body: SetInput = {
      exercise_id: exercise?.id ?? null,
      set_no: parseInt0(setNo),
      reps: parseInt0(reps),
      weight_kg: parseNum(weight),
      rpe: parseNum(rpe),
      rir: parseInt0(rir),
      unilateral_side: side || null,
      notes: notes.trim() || null,
    };
    addSet.mutate(
      { sessionId, body },
      {
        onSuccess: () => {
          if (keepOpen) {
            // Keep exercise + side; advance set number; clear the per-set metrics
            // so the trainer can fire in the next set immediately.
            setSetNo((n) => String((parseInt0(n) ?? nextSetNo) + 1));
            setReps("");
            setWeight("");
            setRpe("");
            setRir("");
            setNotes("");
          } else {
            onClose();
          }
        },
        onError: () => setError(t("sets.saveError")),
      },
    );
  };

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        title={t("sets.add")}
        footer={
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} disabled={addSet.isPending}>
              {t("form.cancel")}
            </Button>
            <div className="flex-1" />
            <Button
              variant="secondary"
              onClick={() => submit(true)}
              disabled={addSet.isPending}
              title={t("sets.add")}
            >
              {addSet.isPending ? t("sets.saving") : `${t("sets.save")} +`}
            </Button>
            <Button onClick={() => submit(false)} disabled={addSet.isPending}>
              {t("sets.save")}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-1">
          {/* Exercise picker trigger */}
          <button
            type="button"
            onClick={() => setPicker(true)}
            className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line-strong shadow-xs transition-shadow hover:ring-volt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
          >
            <span
              className={cn(
                "grid h-11 w-11 shrink-0 place-items-center rounded-md text-white",
                exercise ? "bg-grad-coral" : "bg-grad-ocean",
              )}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                <path d="M6.5 9v6M17.5 9v6M4 12h2.5M17.5 12H20M6.5 12h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("sets.exercise")}
              </span>
              <span className="block truncate font-display text-base font-bold text-body">
                {exercise
                  ? exerciseName(exercise, i18n.language) || `#${exercise.id}`
                  : t("sets.pickExercise")}
              </span>
            </span>
            <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-body-faint" fill="none" aria-hidden>
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Reps + weight — the big, primary inputs */}
          <div className="grid grid-cols-2 gap-3">
            <BigNumberField
              label={t("sets.reps")}
              value={reps}
              onChange={setReps}
              inputMode="numeric"
            />
            <BigNumberField
              label={t("sets.weight")}
              value={weight}
              onChange={setWeight}
              inputMode="decimal"
              suffix={t("sets.kg")}
            />
          </div>

          {/* Live volume preview */}
          <div className="flex items-center justify-between rounded-md bg-surface-sunken px-4 py-3">
            <span className="font-display text-sm font-bold text-body-soft">
              {t("sets.volume")}
            </span>
            <span className="nums font-display text-lg font-extrabold text-body">
              {volume != null ? fmtNum(volume) : "–"}
              <span className="ml-1 text-xs font-bold text-body-faint">
                {t("log.volumeUnit")}
              </span>
            </span>
          </div>

          {/* Secondary metrics */}
          <div className="grid grid-cols-3 gap-3">
            <TextField
              label={t("sets.setNo")}
              type="number"
              inputMode="numeric"
              min={1}
              value={setNo}
              onChange={(e) => setSetNo(e.target.value)}
            />
            <SelectField
              label={t("sets.rpe")}
              placeholder="—"
              value={rpe}
              onChange={(e) => setRpe(e.target.value)}
              options={Array.from({ length: 10 }, (_, i) => ({
                label: String(i + 1),
                value: String(i + 1),
              }))}
            />
            <TextField
              label={t("sets.rir")}
              type="number"
              inputMode="numeric"
              min={0}
              value={rir}
              onChange={(e) => setRir(e.target.value)}
            />
          </div>

          {/* Side */}
          <SelectField
            label={t("sets.side")}
            placeholder={t("sets.sideBoth")}
            value={side}
            onChange={(e) => setSide(e.target.value)}
            options={[
              { label: t("sets.sideLeft"), value: "left" },
              { label: t("sets.sideRight"), value: "right" },
            ]}
          />

          {/* Notes */}
          <TextField
            label={t("sets.notes")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {error && (
            <p className="text-sm font-medium text-danger" role="alert">
              {error}
            </p>
          )}
        </div>
      </Sheet>

      <ExercisePicker
        open={picker}
        onClose={() => setPicker(false)}
        onPick={setExercise}
      />
    </>
  );
}

/** A larger, thumb-friendly numeric input for the primary set metrics. */
function BigNumberField({
  label,
  value,
  onChange,
  inputMode,
  suffix,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  inputMode: "numeric" | "decimal";
  suffix?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block font-display text-sm font-bold text-body">
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          inputMode={inputMode}
          min={0}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "h-16 w-full rounded-md bg-surface text-center font-display text-2xl font-extrabold text-body",
            "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast",
            "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none",
            suffix ? "pr-9" : "",
          )}
        />
        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-bold text-body-faint">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}
